import { SpaceDto } from "@braoshzmvavz/picnube-common";
import {
  Avatar,
  Container,
  Divider,
  Drawer,
  Modal,
  Popover,
  Text,
  Tooltip,
  UnstyledButton,
  useComputedColorScheme,
  useMantineColorScheme,
  useMantineTheme
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { getAuth, signOut } from "firebase/auth";
import React, { useEffect, useState } from "react";
import {
  HiCalendar,
  HiChevronLeft,
  HiChevronRight,
  HiCog,
  HiFolder,
  HiHome,
  HiMap,
  HiMenu,
  HiOutlineLogout,
  HiPlus,
  HiSearch,
  HiUser,
  HiUsers
} from "react-icons/hi";
import {
  HiChevronUpDown
} from "react-icons/hi2";
import { useLocation, useNavigate } from "react-router-dom";
import useApi from "src/services/useApi";
import useAppState from "src/services/useGlobalState";
import { stringToBgAndTextColors } from "src/utils/StringUtils";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import useGlobalState, { globalToken } from "../../globalState";
import { setProfile } from "../../state/profile/profileSlice";
import BottomDrawerSpaceSelector from "../bottomSheet/BottomDrawerSpaceSelector";
import AddSpaceModal from "../modal/AddSpaceModal";
import { openLogoutModal } from "../modal/confirm";
import SpaceSettingsModal from "../modal/SpaceSettingsModal";
import SpaceUsersModal from "../modal/SpaceUsersModal";
import AppLogo from "../static/AppLogo";
import "./Navbar.css";

interface NavbarLinkProps {
  icon?: typeof HiHome;
  iconComponent?: React.ReactNode;
  color?: string;
  label: string;
  active?: boolean;
  onClick?: () => void;
}

export default function Navbar(props: { demo?: boolean }) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { updateSpaceAccess } = useApi();
  const { changeToSpace } = useAppState();

  const [active, setActive] = useState(0);
  const [isSpacesPopoverOpened, setIsSpacesPopoverOpened] = useState(false);
  const [isSpacesPopoverMobileOpened, setIsSpacesPopoverMobileOpened] =
    useState(false);
  const { colorScheme, setColorScheme } = useMantineColorScheme({
    keepTransitions: true,
  });
  const computedColorScheme = useComputedColorScheme("dark");
  const [isCollapsed, setIsCollapsed] = useState<boolean>(localStorage.getItem("navbar_collapsed") == "true");

  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
  const [mobileMenuOpened, { toggle }] = useDisclosure();
  const [opened, { open, close }] = useDisclosure(false);
  //const [profile, setProfile] = useGlobalState(globalProfile);

  const [token, setToken] = useGlobalState(globalToken);

  const location = useLocation();

  const explorerState = useAppSelector((state) => state.explorer);
  const profileState = useAppSelector((state) => state.profile);

  const [
    isSettingsModalOpened,
    { open: openSettingsModal, close: closeSettingsModal },
  ] = useDisclosure(false);
  const [isUsersModalOpened, { open: openUsersModal, close: closeUsersModal }] =
    useDisclosure(false);
  const [
    isAddSpaceModalOpened,
    { open: openAddSpaceModal, close: closeAddSpaceModal },
  ] = useDisclosure(false);

  useEffect(() => {
    localStorage.setItem("navbar_collapsed", JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  const getIconColor = (color: string) => {
    if (color) return color;
    return colorScheme === "dark"
      ? "var(--mantine-color-dark-2)"
      : "var(--mantine-color-dark-2)";
  };

  const getEmail = () => {
    if (props.demo) {
      if (!profileState.profile) return "";
      return profileState.profile.email;
    }

    return getAuth().currentUser?.email;
  };

  const getBillingPlanOrEmail = () => {
    if (props.demo) {
      if (!profileState.profile) return "";
      return profileState.profile.planId;
    }

    return getEmail();
  };

  const getDisplayName = () => {
    if (props.demo) {
      if (!profileState.profile) return "";
      return profileState.profile.name;
    }

    return getAuth().currentUser?.displayName;
  };

  const getPhotoUrl = () => {
    if (props.demo) {
      return "https://picsum.photos/id/10/200/300";
    }

    return getAuth().currentUser?.photoURL;
  };

  const navbarPrimary = [
    {
      path: "/home",
      icon: HiHome,
      label: "Home",
      onClick: () => navigate(props.demo ? "/demo/home" : "/home"),
    },
    {
      path: "/browse",
      icon: HiFolder,
      label: "Explorer",
      isActive: (path) => path.includes("/browse"),
      onClick: () => {
        // If already in explorer, do nothing
        if (location.pathname.includes("/browse")) {
          return;
        }

        // Otherwise, navigate to explorer taking into account the state
        if (explorerState.currentFolderId !== null) {
          navigate(
            `${props.demo ? "/demo" : ""}/browse/${
              explorerState.currentFolderId
            }`
          );
        } else {
          navigate(props.demo ? "/demo/browse" : "/browse");
        }
      },
    },
    /* {
      path: "/gallery",
      icon: HiViewGrid,
      label: "Gallery",
      onClick: () => navigate(props.demo ? "/demo/gallery" : "/gallery"),
    }, */
    {
      path: "/timeline",
      icon: HiCalendar, //HiMiniCalendarDays,
      label: "Timeline",
      onClick: () => navigate("/timeline"),
    },
    {
      path: "/map",
      icon: HiMap,
      label: "Map",
      onClick: () => navigate("/map"),
    },
    /*{
      path: "/albums",
      icon: HiBookOpen,
      label: "Albums",
      onClick: () => navigate("/albums"),
    },*/
    {
      path: "/search",
      icon: HiSearch,
      label: "Search",
      onClick: () => navigate(props.demo ? "/demo/search" : "/search"),
    },
  ];

  const navbarSecondary = [
    /*{
      icon: HiLightningBolt,
      label: "Upgrade account",
      color: colorScheme === "dark" ? "gold" : "goldenrod",
      onClick: () => console.log("upgrade account"),
    },*/ // TODO: add upgrade account page
    /*{
      icon: HiShieldExclamation,
      label: "Admin",
      color:
        colorScheme === "dark"
          ? "var(--mantine-color-orange-7)"
          : "var(--mantine-color-orange-6)",
      onClick: () => navigate("/admin"),
    },*/ // TODO: add admin page
    {
      path: "/account",
      iconComponent: isMobile ? (
        <div></div>
      ) : !isCollapsed ? (
        <div className="flex flex-row justify-start items-center p-4">
          <div
            className="flex flex-row justify-center items-center h-5 w-5 m-auto
        rounded-full overflow-hidden"
          >
            {getPhotoUrl() ? (
              <img src={getPhotoUrl()} alt="" />
            ) : (
              <HiUser
                className="h-full w-full"
                style={{ color: getIconColor(null) }}
              ></HiUser>
            )}
          </div>

          <Container className="flex flex-1">{getDisplayName()}</Container>
        </div>
      ) : (
        <div
          className="flex flex-row justify-center items-center h-5 w-5 m-auto my-4
        rounded-full overflow-hidden"
        >
          {getPhotoUrl() ? (
            <img className="h-full w-full" src={getPhotoUrl()} alt="" />
          ) : (
            <HiUser
              className="h-full w-full"
              style={{ color: getIconColor(null) }}
            ></HiUser>
          )}
        </div>
      ),
      label: "Account",
      onClick: () => navigate(props.demo ? "/demo/account" : "/account"),
    },
    {
      path: "/settings",
      icon: HiCog,
      label: "Settings",
      onClick: () => navigate(props.demo ? "/demo/settings" : "/settings"),
    },
    {
      icon: HiOutlineLogout,
      label: "Logout",
      onClick: () =>
        openLogoutModal(() => {
          signOut(getAuth()).then(() => {
            setProfile(null);
            setToken(null);
            localStorage.removeItem("currentSpace");
            navigate("/login");
          });
        }),
    },
  ];

  // Remove upgrade account action if environment is private
  /*const isPrivateEnv = process.env.REACT_APP_ENVIRONMENT === "private";
  if (isPrivateEnv) {
    navbarSecondary.splice(0, 1);
  }*/

  const NavbarLink = ({
    icon: Icon,
    iconComponent,
    color,
    label,
    active,
    onClick,
  }: NavbarLinkProps) => {
    return (
      <Tooltip
        disabled={isMobile || !isCollapsed}
        label={label}
        position="right"
        withArrow
        arrowOffset={5}
        arrowSize={4}
        color="var(--mantine-color-dark-9)"
        transitionProps={{ duration: 200, transition: "fade" }}
      >
        <UnstyledButton
          onClick={onClick}
          className={
            "flex flex-row border-2 border-grey-300 dark:border-dark-600" +
            " " +
            (computedColorScheme === "dark"
              ? "nav-link--dark"
              : "nav-link--light") +
            (isMobile || !isCollapsed
              ? " rounded-lg"
              : " justify-center items-center")
          }
          data-active={active || undefined}
        >
          {iconComponent ? (
            iconComponent
          ) : (
            <div className="flex flex-row justify-start items-center p-4">
              <Icon
                className="flex w-5 h-5"
                style={{
                  color: getIconColor(color),
                }}
              />
              {isMobile || !isCollapsed ? (
                <Container hidden={!isMobile} className="flex flex-1">
                  {label}
                </Container>
              ) : (
                ""
              )}
            </div>
          )}
        </UnstyledButton>
      </Tooltip>
    );
  };

  const links = navbarPrimary.map((link, index) => (
    <NavbarLink
      {...link}
      key={link.label}
      active={
        link.path === location.pathname ||
        (link?.isActive && link?.isActive(location.pathname))
      }
      onClick={() => {
        if (isMobile) {
          toggle();
        }
        setActive(index);
        link.onClick();
      }}
    />
  ));

  const actions = navbarSecondary.map((link, index) => (
    <NavbarLink
      {...link}
      key={link.label}
      active={link.path === location.pathname}
      onClick={() => {
        if (isMobile) {
          toggle();
        }
        link.onClick();
      }}
    />
  ));

  const getNavbarStyle = () => {
    let baseStyle = isMobile
      ? "flex flex-row justify-between items-center h-14 min-h-14 border-b-2 "
      : "flex flex-col w-full h-full justify-between border-r-2 ";
    const borderStyle =
      computedColorScheme === "dark"
        ? "border-dark-600"
        : "border-grey-300";

    if (!isMobile && !isCollapsed) {
      baseStyle += " w-72";
    }

    return baseStyle + " " + borderStyle;
  };

  const renderMobileAccountButton = () => {
    if (!isMobile && isCollapsed) {
      return actions[0];
    }

    return (
      <div
        className="flex flex-col mt-auto rounded-lg p-4 gap-4 cursor-pointer dark:bg-dark-700 bg-grey-200 dark:hover:bg-dark-900 hover:bg-grey-300"
        onClick={() => {
          if (isMobile) {
            toggle();
          }
          navigate(props.demo ? "/demo/account" : "/account");
        }}
      >
        <div className="flex flex-row justify-start items-center gap-2 p-2 select-none flex-wrap">
          {getPhotoUrl() ? (
            <img
              className="h-10 w-10 min-h-10 min-w-10 m-auto rounded-full overflow-hidden"
              src={getPhotoUrl()}
              alt=""
            />
          ) : (
            <HiUser className="h-10 w-10 min-h-10 min-w-10 m-auto rounded-full overflow-hidden"></HiUser>
          )}

          <div className="flex flex-row justify-between flex-1 flex-start">
            <Container hidden={!isMobile} className="flex flex-col m-0 flex-1">
              <Text fz="lg" fw={500}>
                {getDisplayName()}
              </Text>
              <Text mt={2} fz="md" c="dimmed">
                {getBillingPlanOrEmail()}
              </Text>
            </Container>
            <div className="flex items-center justify">
              <HiChevronRight className="text-3xl" />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSpaceIcon = () => {
    if (!profileState.currentSpace) {
      return <div className="flex p-3"></div>;
    }

    const name = profileState.currentSpace.name.replace(/[^a-zA-Z0-9 ]/g, "");
    if (name == null) return null;

    const firstLetter = name.substring(0, 1);

    return <Avatar key={name} name={name} color="initials" />;

    return (
      <div
        className={`flex justify-center items-center rounded-full w-8 h-8 min-w-8 min-h-8 pb-[2px] relative`}
        style={{
          backgroundColor: stringToBgAndTextColors(name).backgroundColor,
          color: stringToBgAndTextColors(name).textColor,
        }}
      >
        {firstLetter}
        {/* <div className="absolute top-4 left-4 w-full h-full flex justify-center items-center scale-75">
          <HiSwitchHorizontal className="w-4 h-4" style={{}} />
        </div> */}
      </div>
    );
  };

  const renderSpaceButton = () => {
    if (!profileState.currentSpace) {
      return <div className="flex p-3"></div>;
    }

    const name = profileState.currentSpace.name;
    if (name == null) return null;

    return (
      <div className="flex flex-row justify-between items-center py-4 px-4 gap-3 mx-1">
        {renderSpaceIcon()}
        <div className="flex flex-col justify-center items-start">
          <div className="text-md font-bold">{name}</div>
          <div className="text-sm text-grey-600">
            {profileState.currentSpace.description}
          </div>
        </div>
      </div>
    );
  };

  const renderSpaceActions = () => {
    return (
      <div className="w-full flex flex-col">
        <div
          className="flex flex-row justify-start items-center p-2 px-4 gap-2 hoverable-options--dark cursor-pointer rounded-lg mx-1"
          onClick={() => {
            setIsSpacesPopoverOpened(false);
            openSettingsModal();
          }}
        >
          <HiCog></HiCog>
          <div className="text-sm">Settings</div>
        </div>

        <div
          className="flex flex-row justify-start items-center p-2 px-4 gap-2 hoverable-options--dark cursor-pointer rounded-lg mx-1"
          onClick={() => {
            setIsSpacesPopoverOpened(false);
            openUsersModal();
          }}
        >
          <HiUsers></HiUsers>
          <div className="text-sm">Manage users</div>
        </div>
      </div>
    );
  };

  const renderSpaceButtonNotSelected = (space: SpaceDto) => {
    return (
      <div
        id={space.id}
        className="flex flex-row justify-between items-center p-2 px-4 gap-2 hoverable-options--dark cursor-pointer rounded-lg mx-1"
        onClick={() => {
          setIsSpacesPopoverOpened(false);
          setIsSpacesPopoverMobileOpened(false);

          changeToSpace(space);
        }}
      >
        <div className="flex flex-row justify-between items-center gap-2">
          {/* <div
            className="flex justify-center items-center rounded-full min-w-8 min-h-8"
            style={{
              backgroundColor: stringToBgAndTextColors(space.name)
                .backgroundColor,
              color: stringToBgAndTextColors(space.name).textColor,
            }}
          >
            {firstLetter}
          </div> */}
          <Avatar
            key={space.name}
            name={space.name.replace(/[^a-zA-Z0-9 ]/g, "")}
            color="initials"
          />
          <div className="text-md">{space.name}</div>
        </div>

        <HiChevronRight className="text-lg min-w-fit"></HiChevronRight>
      </div>
    );
  };

  const renderOtherSpacesList = () => {
    const otherSpaces = profileState.profile?.spaces
      .filter((space) => space.id !== profileState.currentSpace.id)
      .map((space) => renderSpaceButtonNotSelected(space));

    if (!otherSpaces?.length) {
      return <></>;
    }

    return (
      <>
        {otherSpaces}
        <div className="border-b border-grey-800 w-full h-2" />
      </>
    );
  };

  const renderSpacesPopoverTarget = () => {
    if (!isMobile && !isCollapsed) {
      return (
        <div className="mb-4 rounded-lg border border-dark-500">
          <UnstyledButton
            onClick={() => {
              setActive(0);
              if (isMobile) {
                setIsSpacesPopoverOpened(false);
                setIsSpacesPopoverMobileOpened(!isSpacesPopoverMobileOpened);
              } else {
                setIsSpacesPopoverMobileOpened(false);
                setIsSpacesPopoverOpened(!isSpacesPopoverOpened);
              }
            }}
            className={
              "flex flex-row border-2 border-grey-300 dark:border-dark-600 w-full rounded-lg  " +
              " " +
              (computedColorScheme === "dark"
                ? "nav-link--dark"
                : "nav-link--light")
            }
            data-active={active || undefined}
          >
            <div className="flex flex-1 flex-row justify-between items-center p-3">
              <div className="flex flex-row justify-start items-center gap-4">
                {renderSpaceIcon()}
                <div className="flex flex-col">
                  <Text size="sm" fw={500}>
                    {profileState?.currentSpace?.name}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {profileState?.currentSpace?.description}
                  </Text>
                </div>
              </div>

              <HiChevronUpDown />
            </div>
          </UnstyledButton>
        </div>
      );
    }
    return (
      <div className="">
        <UnstyledButton
          onClick={() => {
            setActive(0);
            if (isMobile) {
              setIsSpacesPopoverOpened(false);
              setIsSpacesPopoverMobileOpened(!isSpacesPopoverMobileOpened);
            } else {
              setIsSpacesPopoverMobileOpened(false);
              setIsSpacesPopoverOpened(!isSpacesPopoverOpened);
            }
          }}
          className={
            "flex flex-row border-2 border-grey-300 dark:border-dark-600" +
            " " +
            (computedColorScheme === "dark"
              ? "nav-link--dark"
              : "nav-link--light") +
            (isMobile ? " rounded-lg" : "")
          }
          data-active={active || undefined}
        >
          <div className="flex flex-row justify-start items-center p-2">
            {renderSpaceIcon()}
          </div>
        </UnstyledButton>
      </div>
    );
  };

  const handleAddSpaceSave = (newSpace: Partial<SpaceDto>) => {
    // TODO: Implement space creation logic
    console.log("New space:", newSpace);
    closeAddSpaceModal();
  };

  const renderSpacesPopoverContent = () => {
    return (
      <div className="max-h-[90vh] overflow-auto">
        <div className="w-full flex flex-col">
          <div className="flex flex-col justify-between items-start">
            {renderSpaceButton()}
            {renderSpaceActions()}
            <div className="border-b border-grey-300 dark:border-dark-600 w-full h-2" />
          </div>
          <div className="flex flex-col py-2">
            {renderOtherSpacesList()}
            <div
              className="flex justify-start items-center gap-2 p-2 px-4 cursor-pointer rounded-lg hoverable-options--dark text-sm mx-1 mt-2"
              onClick={() => {
                setIsSpacesPopoverOpened(false);
                openAddSpaceModal();
              }}
            >
              <HiPlus></HiPlus>
              Add Space
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSpacesPopover = (isMobile: boolean = false) => {
    if (isMobile) {
      return (
        <BottomDrawerSpaceSelector
          onActionClick={() => {}}
          targetElement={
            <UnstyledButton
              onClick={() => {
                setActive(0);
                if (isMobile) {
                  setIsSpacesPopoverOpened(false);
                  setIsSpacesPopoverMobileOpened(!isSpacesPopoverMobileOpened);
                } else {
                  setIsSpacesPopoverMobileOpened(false);
                  setIsSpacesPopoverOpened(!isSpacesPopoverOpened);
                }
              }}
              data-active={active || undefined}
            >
              <div className="flex flex-row justify-start items-center">
                {renderSpaceIcon()}
              </div>
            </UnstyledButton>
          }
        ></BottomDrawerSpaceSelector>
      );
    }

    return (
      <Popover
        width={340}
        position="right-end"
        offset={18}
        withArrow
        arrowPosition="center"
        shadow="md"
        closeOnClickOutside={true}
        opened={isMobile ? isSpacesPopoverMobileOpened : isSpacesPopoverOpened}
        onOpen={() => {
          if (isMobile) {
            setIsSpacesPopoverMobileOpened(true);
            return;
          }
          setIsSpacesPopoverOpened(true);
        }}
        onClose={() => {
          if (isMobile) {
            setIsSpacesPopoverMobileOpened(false);
            return;
          }
          setIsSpacesPopoverOpened(false);
        }}
      >
        <Popover.Target>{renderSpacesPopoverTarget()}</Popover.Target>
        <Popover.Dropdown className="">
          {renderSpacesPopoverContent()}
        </Popover.Dropdown>
      </Popover>
    );
  };

  const renderNavbarCollapseButton = () => {
    if (isMobile) return null;
    return (
      <div className="w-0 m-auto right-[-1rem] hidden flex-row z-20 items-center justify-center navbar-collapse-button">
        <div
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="flex flex-row items-center justify-center w-10 h-10 rounded-full cursor-pointer btn-interactive
      dark:bg-dark-100 bg-white dark:active:bg-dark-400 dark:hover:bg-dark-800 hover:bg-grey-200 active:bg-grey-300"
        >
          {isCollapsed ? (
            <HiChevronRight className="text-2xl" />
          ) : (
            <HiChevronLeft className="text-2xl" />
          )}
        </div>
      </div>
    );
  };

  const getGlobalStyle = () => {
    if (isMobile) {
      return "";
    }
    let style = "flex flex-row relative navbar-root";
    if (!isCollapsed) {
      style += " w-[18rem] min-w-[18rem]";
    }
    return style;
  };

  const handleSpaceSettingsSave = (updatedSpace: SpaceDto) => {
    // TODO: Implement space settings update logic
    console.log("Space settings updated:", updatedSpace);
    closeSettingsModal();
  };

  const handleSpaceUsersSave = (addUsers, removeUsers) => {
    updateSpaceAccess(profileState.currentSpace.id, {
      upsertUsers: addUsers,
      removeUsers,
    }).then((response: any) => {
      console.log("updateSpaceAccess response", response);
      closeUsersModal();
    });
  };

  return (
    <div className={getGlobalStyle()}>
      {/* Desktop */}
      <Container
        visibleFrom="xs"
        p={isCollapsed ? "0.5rem" : "1rem"}
        bg={computedColorScheme === "dark" ? "#111212" : "#ffffff"}
        className={getNavbarStyle()}
      >
        <div className="flex flex-col gap-2">
          {renderSpacesPopover()}
          <div className="flex flex-col gap-1">{links}</div>
        </div>

        <div className="flex flex-col">
          <div className="flex flex-col">{actions}</div>
        </div>
      </Container>

      {/* Mobile */}
      <Container
        hiddenFrom="xs"
        bg={computedColorScheme === "dark" ? "#111212" : "#ffffff"}
        className={getNavbarStyle()}
      >
        <div>
          <div
            onClick={toggle}
            className="flex hoverable-options--dark cursor-pointer
            rounded-xl p-2 ml-[-0.5rem]"
            data-active={active || undefined}
          >
            <HiMenu className="w-5 h-5 text-grey-400" />
          </div>

          <Drawer
            opened={isMobile && mobileMenuOpened}
            onClose={toggle}
            title={
              <div className="flex items-center gap-1 text-xl">
                <AppLogo size={30}></AppLogo>
                <h1 className="h-full">{process.env.REACT_APP_APPNAME}</h1>
              </div>
            }
          >
            <div className="flex-1">
              <div className="flex flex-col">
                <div className="flex flex-col">{links}</div>
              </div>
              <Divider my={18}></Divider>

              <div className="flex flex-col">
                <div className="flex flex-col">{actions}</div>
              </div>
            </div>

            {renderMobileAccountButton()}
          </Drawer>
        </div>
        <div className="flex flex-col justify-center items-center cursor-pointer">
          {renderSpacesPopover(true)}
        </div>
      </Container>

      {renderNavbarCollapseButton()}

      <Modal
        opened={isSettingsModalOpened}
        onClose={closeSettingsModal}
        title={
          <div className="flex flex-row gap-2 items-center">
            <HiCog />
            <Text>Space Settings</Text>
          </div>
        }
      >
        <SpaceSettingsModal
          space={profileState.currentSpace}
          onSave={handleSpaceSettingsSave}
        />
      </Modal>

      <Modal
        opened={isUsersModalOpened}
        onClose={closeUsersModal}
        title={
          <div className="flex flex-row gap-2 items-center">
            <HiUsers />
            <Text>Manage Users</Text>
          </div>
        }
        size="lg"
        fullScreen={isMobile}
      >
        <SpaceUsersModal
          space={profileState.currentSpace}
          onSave={handleSpaceUsersSave}
        />
      </Modal>

      <Modal
        opened={isAddSpaceModalOpened}
        onClose={closeAddSpaceModal}
        title={
          <div className="flex flex-row gap-2 items-center">
            <HiPlus />
            <Text>Add Space</Text>
          </div>
        }
      >
        <AddSpaceModal onSave={handleAddSpaceSave} />
      </Modal>
    </div>
  );
}
